import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { authenticatedFetch } from '@shopify/app-bridge/utilities';
import { discardRejectedAdminApiKey, getAdminApiKey, purgeStaleAdminApiKey } from '../utils/adminKey';
import { redirectToUrl } from '../utils/redirect';
import { toErrorMessage } from '../utils/errorMessage';

// Production API URL - hardcoded to ensure it's always correct in production builds
const API_BASE_URL = import.meta.env.MODE === 'production' 
  ? 'https://ar-space-commerce-production.up.railway.app'
  : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000');

// App Bridge authenticatedFetch attaches `Authorization: Bearer <session token>`
// to every request. NOTE: it must be imported from '@shopify/app-bridge/utilities'
// (the package root does not export it), and via a static ESM import — a
// runtime `require()` is undefined in the Vite browser bundle and silently
// left every request without a session token.
const authenticatedFetchFn: (app: any) => typeof fetch = authenticatedFetch;

// Store App Bridge instance globally (set by AppBridgeProvider)
let globalAppBridge: any = null;

export function setAppBridgeInstance(app: any) {
  globalAppBridge = app;
}

// Accessor so billing redirects can reuse the App Bridge instance. Redirecting
// to a Shopify charge-confirmation URL from inside the embedded iframe MUST go
// through App Bridge REMOTE redirect — a bare `window.location.href` is blocked
// by the Admin's frame-ancestors CSP, so the merchant clicks "Subscribe" and
// nothing happens (this was the "payment doesn't work" bug).
export function getAppBridge(): any {
  return globalAppBridge;
}

// Create axios instance with App Bridge authenticated fetch support
export const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Include cookies for session management
  });

  // Helper function to sanitize header values for ISO-8859-1 compatibility
  const sanitizeHeaderValue = (value: string | null | undefined): string | null => {
    if (!value) return null;
    // Remove any characters outside ISO-8859-1 range (0x00-0xFF)
    const sanitized = String(value).replace(/[^\x00-\xFF]/g, '');
    return sanitized || null;
  };

  // Add request interceptor to use App Bridge authenticated fetch if available
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Extract shop domain from URL params or App Bridge
      const urlParams = new URLSearchParams(window.location.search);
      const shopDomain = urlParams.get('shop') || urlParams.get('host');
      
      // Add shop domain to headers if available
      if (shopDomain) {
        // Extract shop domain from host if it's base64 encoded
        let actualShopDomain = shopDomain;
        try {
          // Host is base64 encoded: YWRtaW4uc2hvcGlmeS5jb20vc3RvcmUvYXItc3BhY2UtcGxhdGZvcm0
          // Decode and extract shop domain
          const decoded = atob(shopDomain);
          // Format: admin.shopify.com/store/shop-name
          const match = decoded.match(/store\/([^\/]+)/);
          if (match) {
            actualShopDomain = `${match[1]}.myshopify.com`;
          }
        } catch (e) {
          // Not base64, use as is
          if (shopDomain.includes('.myshopify.com')) {
            actualShopDomain = shopDomain;
          }
        }
        
        // Sanitize and set header
        const sanitizedDomain = sanitizeHeaderValue(actualShopDomain);
        if (sanitizedDomain) {
          config.headers['X-Shopify-Shop-Domain'] = sanitizedDomain;
          console.debug('API Client: Added X-Shopify-Shop-Domain header:', sanitizedDomain);
        }
      } else {
        console.debug('API Client: No shop domain found in URL params (shop or host)');
      }
      
      // Inject the admin API key ONLY when there is no App Bridge session.
      // ShopAuthGuard accepts either credential, but a stored admin key can
      // belong to a different (or synthetic `settings-only`) shop, and it wins
      // over the session token — resolving every request against the wrong
      // shop (empty catalog, billing checkout 400s). Embedded → session token
      // only; the key stays as a non-embedded / local-dev fallback.
      if (globalAppBridge) {
        purgeStaleAdminApiKey();
      } else {
        const adminKey = getAdminApiKey();
        if (adminKey) {
          const sanitizedKey = sanitizeHeaderValue(adminKey);
          if (sanitizedKey) {
            (config.headers as any)['x-admin-api-key'] = sanitizedKey;
          }
        }
      }
      
      // Sanitize all existing headers to ensure ISO-8859-1 compatibility
      // This prevents XMLHttpRequest errors when axios validates headers
      const headers = config.headers || {};
      Object.keys(headers).forEach((key) => {
        const value = (headers as any)[key];
        if (value != null) {
          const sanitized = sanitizeHeaderValue(String(value));
          if (sanitized !== null && sanitized !== String(value)) {
            (headers as any)[key] = sanitized;
          }
        }
      });

      // Default axios instance sets Content-Type: application/json. That breaks multipart
      // when the XHR adapter is used (App Bridge not active / adapter not attached).
      // Remove it so axios sets multipart/form-data with boundary for FormData bodies.
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        const h = config.headers as any;
        if (h?.delete) {
          h.delete('Content-Type');
          h.delete('content-type');
        } else {
          delete h['Content-Type'];
          delete h['content-type'];
        }
      }

      // Use App Bridge authenticated fetch if available
      if (globalAppBridge && authenticatedFetchFn) {
        try {
          const fetch = authenticatedFetchFn(globalAppBridge);
          
          // Override the adapter to use authenticated fetch
          config.adapter = async (config: InternalAxiosRequestConfig) => {
            // Ensure we use the full URL with baseURL
            let url = config.url || '';
            if (url.startsWith('/')) {
              // If URL starts with /, prepend baseURL
              url = `${config.baseURL || API_BASE_URL}${url}`;
            } else if (!url.startsWith('http')) {
              // If URL doesn't start with http, prepend baseURL
              url = `${config.baseURL || API_BASE_URL}/${url}`;
            }
            
            const isFormData =
              typeof FormData !== 'undefined' && config.data instanceof FormData;

            // Sanitize headers to ensure ISO-8859-1 compatibility.
            // Do not default to JSON for multipart uploads — App Bridge fetch must receive
            // FormData as the body and must NOT set Content-Type (browser adds boundary).
            const sanitizedHeaders: Record<string, string> = {};
            if (!isFormData) {
              sanitizedHeaders['Content-Type'] = 'application/json';
            }

            // Copy and sanitize all headers
            Object.keys(config.headers || {}).forEach((key) => {
              const value = (config.headers as any)[key];
              if (value != null) {
                // Convert to string and remove non-ISO-8859-1 characters
                const stringValue = String(value);
                const sanitized = stringValue.replace(/[^\x00-\xFF]/g, '');
                if (sanitized) {
                  sanitizedHeaders[key] = sanitized;
                }
              }
            });

            if (isFormData) {
              delete sanitizedHeaders['Content-Type'];
              delete sanitizedHeaders['content-type'];
            }

            const options: RequestInit = {
              method: (config.method?.toUpperCase() || 'GET') as any,
              headers: sanitizedHeaders,
            };

            // Remove Content-Type header if no body (GET requests)
            if (!config.data && (config.method?.toUpperCase() === 'GET' || !config.method)) {
              delete (options.headers as any)['Content-Type'];
            }

            if (config.data) {
              if (isFormData) {
                options.body = config.data as FormData;
              } else {
                options.body = JSON.stringify(config.data);
              }
            }

            const response = await fetch(url, options);
            
            // Convert Headers to plain object so interceptors can read header values
            const headersObj: Record<string, string> = {};
            response.headers.forEach((value, key) => {
              headersObj[key.toLowerCase()] = value;
            });

            let data;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              data = await response.json();
            } else {
              data = await response.text();
            }

            const axiosResponse = {
              data,
              status: response.status,
              statusText: response.statusText,
              headers: headersObj,
              config: config as any,
            };

            // Axios adapters MUST reject on non-2xx, otherwise the response
            // interceptor (401 reauthorize) and every caller's catch never fire.
            const validate =
              (config as any).validateStatus ?? ((s: number) => s >= 200 && s < 300);
            if (!validate(response.status)) {
              const err = new Error(
                `Request failed with status code ${response.status}`
              ) as any;
              err.isAxiosError = true;
              err.config = config;
              err.response = axiosResponse;
              err.code = response.status >= 500 ? 'ERR_BAD_RESPONSE' : 'ERR_BAD_REQUEST';
              throw err;
            }

            return axiosResponse;
          };
        } catch (e) {
          // App Bridge not available, continue with regular axios
          console.debug('App Bridge authenticated fetch failed, using regular axios');
        }
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add error handling
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        const headers = error.response.headers || {};

        if (status === 401) {
          const reauthUrl =
            (typeof headers.get === 'function' ? headers.get('x-shopify-api-request-failure-reauthorize-url') : null) ||
            headers['x-shopify-api-request-failure-reauthorize-url'];
          if (reauthUrl) {
            redirectToUrl(reauthUrl, globalAppBridge);
            return Promise.reject(error);
          }
          // No reauthorize header: the request wasn't rejected for a stale
          // Shopify session, so if we sent a stored admin key it is the thing
          // being refused. Keeping it would retry the same 401 indefinitely.
          discardRejectedAdminApiKey();
          console.error('Unauthorized - session may have expired');
        } else if (status === 403) {
          console.error('Forbidden - insufficient permissions');
        } else if (status === 404) {
          console.error('Resource not found:', error.config?.url);
        } else if (status >= 500) {
          console.error('Server error:', toErrorMessage(error, `HTTP ${status}`), data);
        } else {
          console.error('API Error:', toErrorMessage(error, `HTTP ${status}`), data);
        }
      } else if (error.request) {
        console.error('Network Error: No response from server. Is the backend running?');
      } else {
        console.error('Error:', error.message);
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Singleton instance
let apiClient: AxiosInstance | null = null;

export const getApiClient = (): AxiosInstance => {
  if (!apiClient) {
    apiClient = createApiClient();
  }
  return apiClient;
};

// Helper for App Bridge authenticated requests against the backend.
// IMPORTANT: this only ever calls ${API_BASE_URL} (the NestJS backend on
// Railway). It does NOT call cloudflare-worker-r2-gateway directly — all
// asset uploads go through the backend, which signs them and proxies to R2.
// Keep this invariant; any direct r2-gateway call from the admin would
// duplicate auth and rate-limit logic.
export const createAuthenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Use App Bridge authenticated fetch if available
  if (globalAppBridge && authenticatedFetchFn) {
    try {
      const fetch = authenticatedFetchFn(globalAppBridge);
      return fetch(`${API_BASE_URL}${url}`, options);
    } catch (e) {
      console.debug('App Bridge authenticated fetch failed, using regular fetch');
    }
  }
  
  // Fallback to regular fetch
  return fetch(`${API_BASE_URL}${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
};

